import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * Minimal reads of the organization catalog — coloca-nest `OrganizationsController`
 * (`/internal/organizations`).
 *
 * **This endpoint binds `PaginationDto` and nothing else.** The controller's handler takes `@Query()
 * paginationDto: PaginationDto`, and the app runs a global `ValidationPipe({ whitelist: true,
 * forbidNonWhitelisted: true })` — so `?search=`, `?status=` or any other key is a **400**, not an ignored
 * param. That single fact is why this module exists instead of reusing
 * `modules/dashboard/cards/services/organizationsService.ts`, which builds `search` / `status` /
 * `card_provider` params the API cannot accept.
 *
 * There is **no batch-by-ids endpoint and no organization search endpoint** anywhere in the backend, so the
 * only two ways to turn an `organization_id` into a name are: page this catalog, or read one org at a time.
 * `useOrganizationDirectory` does both — see that hook for the strategy.
 *
 * Tenancy differs between the two calls and it matters:
 *
 * - `GET /internal/organizations` is `organizations:read` **+ `userTypes: [INTERNAL]`** — an organization
 *   member gets 403.
 * - `GET /internal/organizations/:id` is `organizations:read` with **no** `userTypes`; a member may read their
 *   own organization and is refused any other.
 */

/** The projection this app needs. The endpoint returns the whole entity; we deliberately keep three fields. */
export interface OrganizationSummary {
  id: string;
  name: string;
  /**
   * Marks the single Coloca-owned tenant. Every INTERNAL user is pinned to it by migration, so this is how a
   * staff row is recognised — never by matching the name string, which is editable through `PATCH`.
   */
  is_root: boolean;
}

export interface OrganizationPage {
  data: OrganizationSummary[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/** The backend's `PaginationDto` ceiling (`@Max(200)`); asking for more is a 400. */
export const ORGANIZATIONS_PAGE_LIMIT = 200;

const ORGANIZATIONS_URL = "/internal/organizations";

/** Keeps only the three fields we use, so a 200-row page does not retain 200 `card_styles_config` blobs. */
function toSummary(raw: OrganizationSummary): OrganizationSummary {
  return { id: raw.id, name: raw.name, is_root: raw.is_root === true };
}

export const OrganizationsDirectoryService = {
  /** One page of the catalog. Only `skip` and `limit` may be sent. */
  async listPage(skip: number, limit: number = ORGANIZATIONS_PAGE_LIMIT): Promise<OrganizationPage> {
    const { data } = await HttpApiService.get<OrganizationPage>(
      `${ORGANIZATIONS_URL}?skip=${skip}&limit=${limit}`,
    );
    return {
      data: (data.data ?? []).map(toSummary),
      total: data.total ?? 0,
      skip: data.skip ?? skip,
      limit: data.limit ?? limit,
      hasMore: data.hasMore === true,
    };
  },

  /** One organization by id. 404 when it is soft-deleted, 403 when a member asks for someone else's. */
  async getById(id: string): Promise<OrganizationSummary> {
    const { data } = await HttpApiService.get<OrganizationSummary>(
      `${ORGANIZATIONS_URL}/${encodeURIComponent(id)}`,
    );
    return toSummary(data);
  },
};
